import { getCustomFieldSettings } from "./customSettings.js";
import { escapeFieldName } from "./objectPaths.js";
/**
 * Parses the fields from a shape.
 *
 * @param shape The shape to parse the fields from.
 * @param parent The parent of the fields
 * @returns An array of the parsed fields.
 */
export function parseFields(shape, parent = null) {
    return Object.entries(shape).map(([field, settings]) => {
        return parseField(field, settings, parent);
    });
}
/**
 * Parses a schema from a shape.
 *
 * @param shape The shape to parse the schema from.
 * @returns The parsed schema.
 */
export function parseSchema(shape) {
    const fields = parseFields(shape, null);
    return { schema: shape, fields };
}
/**
 * Parses a field from a shape.
 *
 * @param field The field name.
 * @param settings The ZodType information for the field.
 * @param parent The parent of the field
 * @returns The parsed field.
 */
export function parseField(field, settings, parent) {
    const originalDefinition = settings._def;
    const description = originalDefinition.description;
    const { typeDefinition: definition, defaultValue, optional, } = getActualField(originalDefinition);
    const escapedField = escapeFieldName(field);
    const path = parent?.path ? `${parent.path}.${escapedField}` : escapedField;
    let children;
    let options;
    if (definition.typeName === "ZodUnion" &&
        "options" in definition &&
        definition.options) {
        options = definition.options.map((d) => {
            const fieldSettings = getCustomFieldSettings(d._def.description || "");
            return {
                ...d._def,
                settings: fieldSettings || undefined,
            };
        });
    }
    const fieldSettings = getCustomFieldSettings(description);
    const baseReturn = {
        field: escapedField,
        definition,
        optional,
        defaultValue,
        description,
        settings: fieldSettings || undefined,
        path,
        parent,
    };
    if (definition.typeName === "ZodObject" ||
        definition.typeName === "ZodArray") {
        const innerShape = definition.shape
            ? definition.shape()
            : definition.type._def.shape();
        children = parseFields(innerShape, baseReturn);
    }
    if (children) {
        Object.assign(baseReturn, { children });
    }
    if (definition.typeName === "ZodArray") {
        Object.assign(baseReturn, { isArray: true });
    }
    if (definition.typeName === "ZodObject") {
        Object.assign(baseReturn, { isArray: false });
    }
    if (options) {
        Object.assign(baseReturn, { options });
    }
    return baseReturn;
}
/**
 * Gets the actual field definition from a meta-field.
 *
 * @param definition The field definition.
 * @returns The actual field definition with the default and optional
 *   information from the shell.
 */
function getActualField(definition) {
    let optional = false;
    let defaultValue = null;
    let typeDefinition = definition;
    while (isMetaField(typeDefinition.typeName)) {
        optional = optional || typeDefinition.typeName === "ZodOptional";
        if (typeDefinition.typeName === "ZodDefault" &&
            typeDefinition.defaultValue) {
            defaultValue = typeDefinition.defaultValue();
        }
        typeDefinition = typeDefinition.innerType._def;
    }
    return { typeDefinition, defaultValue, optional };
}
/**
 * Checks if a field is a meta field meaning that there is another field nested
 * inside.
 *
 * @param fieldType The type of the field.
 * @returns True if the field is a meta field.
 */
function isMetaField(fieldType) {
    return fieldType === "ZodOptional" || fieldType === "ZodDefault";
}
//# sourceMappingURL=schemaParser.js.map